import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Droplet, Clock, CheckCircle2, AlertCircle, TrendingUp, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { useRealtimeRequests } from '../lib/useRealtimeRequests';
import { useNotification, MuteButton } from '../components/NotificationProvider';
import { useBranchStore } from '../store/branchStore';
import { useBranchChangeListener } from '../lib/branchChangeEvent';

interface DashboardStats {
  open: number;
  inProgress: number;
  pending: number;
  completed: number;
  averageResponseTime: number | null;
  averageLeakRunningTime?: number | null;
  averageSupplyInterruptionTime?: number | null;
  averageRepairDuration?: number | null;
  averageTotalProcessingTime?: number | null;
}

interface ServiceRequest {
  id: string;
  ticketNumber: string;
  description: string;
  status: string;
  location: string;
  branchName?: string;
  createdAt: string;
}

/**
 * Format duration in minutes with smart unit selection and rounding
 * < 60 min: shows minutes (rounded to nearest 5)
 * 60 min - 24 hours: shows hours and minutes (rounds up if minutes >= 30)
 * >= 24 hours: shows only days (rounded to nearest day)
 */
function formatDuration(minutes: number | null): string {
  if (minutes === null) return '';

  // Round to nearest 5 minutes for small durations
  if (minutes < 60) {
    const rounded = Math.round(minutes / 5) * 5;
    return `${rounded}min`;
  }

  // Less than 1440 minutes (24 hours) - show hours and round
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    // If minutes >= 30, round up to next hour
    if (mins >= 30) {
      return `${hours + 1}h`;
    }
    return `${hours}h`;
  }

  // 24 hours or more - show only days (rounded to nearest day)
  const days = Math.round(minutes / 1440);
  return `${days}d`;
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();
  const activeBranch = useBranchStore((state) => state.activeBranch);
  const activeBranchId = activeBranch?.id;

  // Fetch functions - wrapped in useCallback to stabilize for polling hook
  const fetchStats = useCallback(async (branchId?: string) => {
    const branch = branchId || activeBranchId;
    if (!branch) {
      console.log('[Dashboard] No active branch, skipping stats fetch');
      return;
    }
    try {
      console.log('[Dashboard] Fetching stats for branch:', branch);
      const params = `?branchId=${branch}`;
      const data = await api.get<DashboardStats>(`/requests/dashboard/stats${params}`);
      console.log('[Dashboard] Received stats:', data);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  const fetchRequests = useCallback(async (branchId?: string) => {
    const branch = branchId || activeBranchId;
    if (!branch) {
      console.log('[Dashboard] No active branch, skipping requests fetch');
      return;
    }
    try {
      console.log('[Dashboard] Fetching requests for branch:', branch);
      const params = `?branchId=${branch}`;
      const data = await api.get<{ requests: ServiceRequest[] }>(`/requests${params}`);
      console.log('[Dashboard] Received', data.requests.length, 'requests for branch:', branch);
      setRequests(data.requests);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  }, [activeBranchId]);

  // Listen for branch change events from anywhere in the app
  // The event provides the branch, so we don't need to wait for state update
  useBranchChangeListener(useCallback((branch) => {
    console.log('[Dashboard] Branch change event received:', branch.id, branch.name);
    setLoading(true);
    // Fetch data directly for the branch in the event (not relying on activeBranchId state)
    (async () => {
      try {
        const params = `?branchId=${branch.id}`;
        console.log('[Dashboard] Event: Fetching stats for branch:', branch.id);
        const statsData = await api.get<DashboardStats>(`/requests/dashboard/stats${params}`);
        setStats(statsData);

        console.log('[Dashboard] Event: Fetching requests for branch:', branch.id);
        const reqData = await api.get<{ requests: ServiceRequest[] }>(`/requests${params}`);
        setRequests(reqData.requests);
        setLoading(false);
      } catch (error) {
        console.error('[Dashboard] Event fetch failed:', error);
        setLoading(false);
      }
    })();
  }, []));

  // Initial load and branch change via store
  useEffect(() => {
    console.log('[Dashboard] useEffect triggered for branch:', activeBranchId);
    if (activeBranchId) {
      setLoading(true);
      fetchStats(activeBranchId);
      fetchRequests(activeBranchId);
    } else {
      // Try to get branch from cache if not in store yet
      try {
        const cached = localStorage.getItem('cached_branches');
        if (cached) {
          const { defaultBranch } = JSON.parse(cached);
          if (defaultBranch?.id) {
            console.log('[Dashboard] Using cached branch:', defaultBranch.name);
            setLoading(true);
            fetchStats(defaultBranch.id);
            fetchRequests(defaultBranch.id);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.error('[Dashboard] Failed to load cached branch:', e);
        setLoading(false);
      }
    }
  }, [activeBranchId, fetchStats, fetchRequests]);

  // Subscribe to realtime request updates
  useRealtimeRequests({
    branchId: activeBranchId,
    onInitialLoad: (initialRequests) => {
      console.log('[Dashboard] Realtime: Initial load of', initialRequests.length, 'requests');
      setRequests(initialRequests);
      setLoading(false);
    },
    onNewRequest: (request) => {
      console.log('[Dashboard] Realtime: New request detected:', request.ticketNumber);
      setRequests((prev) => [request, ...prev]);
      showNotification('info', `New request: ${request.ticketNumber} (#${request.description})`);
    },
    onStatusChange: (request, oldStatus, newStatus) => {
      console.log('[Dashboard] Realtime: Status changed:', request.ticketNumber, `${oldStatus} → ${newStatus}`);
      setRequests((prev) =>
        prev.map((req) =>
          req.id === request.id ? { ...req, status: newStatus } : req
        )
      );
      const statusLabels: Record<string, string> = {
        open: 'Open',
        in_progress: 'In Progress',
        pending: 'Pending',
        completed: 'Completed',
      };
      showNotification(
        'success',
        `Status updated: ${request.ticketNumber} - ${statusLabels[oldStatus] || oldStatus} → ${statusLabels[newStatus] || newStatus}`
      );
    },
    onUpdate: (updateBranchId) => {
      // Refresh stats when data changes
      if (updateBranchId) {
        (async () => {
          try {
            const params = `?branchId=${updateBranchId}`;
            const statsData = await api.get<DashboardStats>(`/requests/dashboard/stats${params}`);
            setStats(statsData);
          } catch (error) {
            console.error('[Dashboard] Failed to refresh stats:', error);
          }
        })();
      }
    },
  });

  const activeRequests = requests.filter(
    (req) => req.status === 'open' || req.status === 'in_progress'
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_progress': return 'bg-amber-500';
      case 'pending': return 'bg-orange-500';
      case 'completed': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Show loading if no branch is set yet (waiting for branch preload)
  if (!activeBranchId && loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome to the Water Management Platform</p>
        </div>
        <MuteButton />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <Link to="/requests?filter=open" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md hover:border-cyan-300 transition-all cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <Droplet className="h-8 w-8 text-blue-600" />
            <span className="text-3xl font-bold text-blue-600">{stats?.open ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Open Requests</h3>
        </Link>

        <Link to="/requests?filter=in_progress" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md hover:border-cyan-300 transition-all cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <Clock className="h-8 w-8 text-amber-600" />
            <span className="text-3xl font-bold text-amber-600">{stats?.inProgress ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600">In Progress</h3>
        </Link>

        <Link to="/requests?filter=pending" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md hover:border-cyan-300 transition-all cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <AlertCircle className="h-8 w-8 text-orange-600" />
            <span className="text-3xl font-bold text-orange-600">{stats?.pending ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Pending</h3>
        </Link>

        <Link to="/requests?filter=completed" className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md hover:border-cyan-300 transition-all cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <span className="text-3xl font-bold text-emerald-600">{stats?.completed ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-600">Completed</h3>
        </Link>
      </div>

      {/* Performance Metrics */}
      {(stats?.averageResponseTime || stats?.averageLeakRunningTime || stats?.averageSupplyInterruptionTime || stats?.averageRepairDuration || stats?.averageTotalProcessingTime) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {stats?.averageResponseTime !== null && stats?.averageResponseTime !== undefined && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-cyan-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Avg. Response Time</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(Math.round(stats.averageResponseTime))}</p>
              <p className="text-xs text-gray-500 mt-1">Report → On Site</p>
              <p className="text-xs text-gray-400 mt-2">1 month</p>
            </div>
          )}

          {stats?.averageLeakRunningTime !== null && stats?.averageLeakRunningTime !== undefined && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Avg. Leak Running Time</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(Math.round(stats.averageLeakRunningTime))}</p>
              <p className="text-xs text-gray-500 mt-1">Report → Shutdown</p>
              <p className="text-xs text-gray-400 mt-2">1 month</p>
            </div>
          )}

          {stats?.averageSupplyInterruptionTime !== null && stats?.averageSupplyInterruptionTime !== undefined && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Avg. Supply Interruption</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(Math.round(stats.averageSupplyInterruptionTime))}</p>
              <p className="text-xs text-gray-500 mt-1">Supply Interruption</p>
              <p className="text-xs text-gray-400 mt-2">1 month</p>
            </div>
          )}

          {stats?.averageRepairDuration !== null && stats?.averageRepairDuration !== undefined && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Avg. Repair Duration</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(Math.round(stats.averageRepairDuration))}</p>
              <p className="text-xs text-gray-500 mt-1">Repair Duration</p>
              <p className="text-xs text-gray-400 mt-2">1 month</p>
            </div>
          )}

          {stats?.averageTotalProcessingTime !== null && stats?.averageTotalProcessingTime !== undefined && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Avg. Total Processing Time</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(Math.round(stats.averageTotalProcessingTime))}</p>
              <p className="text-xs text-gray-500 mt-1">Report → Done</p>
              <p className="text-xs text-gray-400 mt-2">1 month</p>
            </div>
          )}
        </div>
      )}

      {/* Active Requests */}
      <div className="mt-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 leading-none">Active Requests</h1>

        {activeRequests.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <Droplet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No active requests</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeRequests.map((request) => (
              <Link
                key={request.id}
                to={`/requests/${request.id}`}
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 hover:shadow-md hover:border-cyan-300 transition-all flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-700">
                      {request.ticketNumber}
                    </h3>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-3 line-clamp-2 flex-1">
                  {request.description}
                </p>

                <div className="flex items-center justify-between mt-auto">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(request.status)}`} />
                    <span className="text-sm text-gray-600">{formatStatus(request.status)}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate max-w-[120px]">{request.location}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
